/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { ArrowUp, ArrowDown, ArrowsClockwise, Lightning, ChartLine, Pencil } from "@phosphor-icons/react";

// Inherit the same SLATE palette as AdminAutoBlogPage.
const TH = {
  bg: "#0F1623", surface: "#172234", surfaceHi: "#1F2D45",
  border: "#2B3A56", borderHi: "#3A4E72",
  accent: "#F5C16C", accentDim: "#8C6638",
  text: "#EAF0FA", textMute: "#90A1BD", textDim: "#566A8A",
  green: "#4ADE9C", red: "#FB7185", purple: "#A78BFA", blue: "#60A5FA",
};

function fmtN(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

// Aggregate daily timeseries into weekly buckets for the line chart.
function bucketWeekly(rows) {
  const buckets = {};
  rows.forEach((r) => {
    const d = new Date(r.date);
    // ISO week start = Monday.
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() - day + 1);
    const k = d.toISOString().slice(0, 10);
    if (!buckets[k]) buckets[k] = { week: k, clicks: 0, impressions: 0 };
    buckets[k].clicks += r.clicks || 0;
    buckets[k].impressions += r.impressions || 0;
  });
  return Object.values(buckets).sort((a, b) => (a.week < b.week ? -1 : 1));
}

function bucketMonthly(rows) {
  const buckets = {};
  rows.forEach((r) => {
    const k = (r.date || "").slice(0, 7); // YYYY-MM
    if (!k) return;
    if (!buckets[k]) buckets[k] = { month: k, clicks: 0, impressions: 0 };
    buckets[k].clicks += r.clicks || 0;
    buckets[k].impressions += r.impressions || 0;
  });
  return Object.values(buckets).sort((a, b) => (a.month < b.month ? -1 : 1));
}

function DeltaPill({ value }) {
  if (value == null) return <span className="text-[10px]" style={{ color: TH.textDim }}>—</span>;
  const up = value >= 0;
  return (
    <span className="text-[10px] inline-flex items-center gap-0.5" style={{ color: up ? TH.green : TH.red }}>
      {up ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
      {Math.abs(value)}%
    </span>
  );
}

export default function AutoBlogAnalyticsTab() {
  const [data, setData] = useState(null);
  const [needsRewrite, setNeedsRewrite] = useState({ connected: false, rows: [] });
  const [history, setHistory] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, n, h] = await Promise.all([
        api.get("/super-admin/autoblog/analytics?days=28"),
        api.get("/super-admin/autoblog/needs-rewrite"),
        api.get("/super-admin/autoblog/rewrite-history"),
      ]);
      setData(a.data);
      setNeedsRewrite(n.data || { rows: [] });
      setHistory(h.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // If we're returning from the Google consent screen, show a toast and refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gsc_connected") === "1") {
      toast.success("Google Search Console connected.");
      // Strip the param so a refresh doesn't re-toast.
      const u = new URL(window.location.href);
      u.searchParams.delete("gsc_connected");
      window.history.replaceState({}, "", u.toString());
      load();
    } else if (params.get("gsc_error")) {
      const code = params.get("gsc_error");
      toast.error(
        code === "no_refresh_token"
          ? "Google did not issue a refresh token. Visit myaccount.google.com/permissions, remove AstroKuberChat, then click Connect again."
          : `GSC connect failed: ${code}`,
      );
      const u = new URL(window.location.href);
      u.searchParams.delete("gsc_error");
      window.history.replaceState({}, "", u.toString());
    }
  }, [load]);

  const connectGsc = () => {
    // Redirect to the Google consent screen via our own /auth/google/start
    // helper. The callback stores the refresh_token in `gsc_connection` and
    // bounces back to /admin/autoblog?gsc_connected=1.
    const next = encodeURIComponent("/admin/autoblog?tab=analytics");
    window.location.href = `/api/auth/google/start?gsc=1&next=${next}`;
  };

  const disconnectGsc = async () => {
    if (!window.confirm("Disconnect Google Search Console? Analytics charts will be empty until you reconnect.")) return;
    try {
      await api.post("/super-admin/autoblog/gsc/disconnect");
      toast.success("Disconnected.");
      setSites([]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
  };

  const loadSites = useCallback(async () => {
    if (!data?.connected || data?.mode !== "oauth") return;
    setSitesLoading(true);
    try {
      const { data: s } = await api.get("/super-admin/autoblog/gsc/sites");
      setSites(s?.sites || []);
    } catch (e) {
      // Silent — non-blocking.
      console.warn("sites load failed", e);
    } finally { setSitesLoading(false); }
  }, [data?.connected, data?.mode]);

  useEffect(() => { loadSites(); }, [loadSites]);

  const selectSite = async (siteUrl) => {
    try {
      await api.post("/super-admin/autoblog/gsc/select-site", { site_url: siteUrl });
      toast.success(`Now tracking ${siteUrl}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to switch property"); }
  };

  const weekly = useMemo(() => bucketWeekly(data?.timeseries || []), [data]);
  const monthly = useMemo(() => bucketMonthly(data?.timeseries || []), [data]);

  const rewritePost = async (post_id, title) => {
    if (!window.confirm(`Rewrite "${title}" with a fresh angle? (~₹0.40 LLM cost, ~60s.)`)) return;
    setBusyId(post_id);
    try {
      const { data: r } = await api.post(`/super-admin/autoblog/rewrite/${post_id}`);
      toast.success(`Rewritten! Fresh angle: ${r.fresh_angle_keyword}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewrite failed");
    } finally {
      setBusyId("");
    }
  };

  if (loading && !data) {
    return <div className="py-10 text-center text-sm" style={{ color: TH.textMute }}>Loading analytics…</div>;
  }
  if (!data) return null;

  // ── Banner when GSC isn't connected ───────────────────────────────
  if (!data.connected) {
    return (
      <div className="space-y-6" data-testid="analytics-not-connected">
        <div className="p-6 rounded-2xl border" style={{ background: TH.surfaceHi, borderColor: TH.accent + "60" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: TH.accent + "20", color: TH.accent }}>
              <ChartLine size={22} weight="fill" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: TH.accent }}>One click</div>
              <h3 className="text-lg font-medium">Connect Google Search Console</h3>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: TH.textMute }}>
            Sign in with the Google account that owns the Search Console
            property for <strong style={{ color: TH.text }}>{data.property_url}</strong>.
            We&apos;ll pull your real clicks, impressions, position and
            ranking pages — and use them to flag posts that need a rewrite.
            Read-only access; you can disconnect any time.
          </p>
          <button
            onClick={connectGsc}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm tracking-wider uppercase transition-all hover:opacity-90"
            style={{ background: TH.accent, color: TH.bg }}
            data-testid="connect-gsc-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 015.5 12c0-.73.13-1.44.35-2.09V7.07H2.18A11 11 0 001 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            <span>Connect with Google</span>
          </button>
          <p className="mt-3 text-xs" style={{ color: TH.textDim }}>
            Property tracked: <strong style={{ color: TH.accent }}>{data.property_url}</strong>
            {" · "}
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: TH.accent }}
            >
              Open Search Console
            </a>
          </p>
        </div>
        <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }}>
          <div className="text-sm" style={{ color: TH.textMute }}>
            Until GSC is connected, the Rewrite engine has no signal to flag underperforming posts.
            All other AutoBlog features (plan, daily publish, manual rewrite trigger via Mongo) keep working.
          </div>
        </div>
      </div>
    );
  }

  // ── Connected — render the full Analytics view ────────────────────
  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="analytics-connected">
      {/* Connection status strip */}
      <div
        className="p-3 rounded-xl border flex flex-wrap items-center gap-3 text-xs"
        style={{ background: TH.surface, borderColor: TH.border, color: TH.textMute }}
        data-testid="gsc-connection-strip"
      >
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full"
          style={{ background: TH.green + "18", color: TH.green }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: TH.green }} />
          GSC connected
        </span>
        <span>
          via <strong style={{ color: TH.text }}>
            {data.mode === "oauth"
              ? (data.oauth_email || "Google account")
              : (data.service_account_email || "service account")}
          </strong>
        </span>
        {data.mode === "oauth" && sites.length > 1 ? (
          <label className="inline-flex items-center gap-2">
            <span>Property:</span>
            <select
              value={data.property_url}
              onChange={(e) => selectSite(e.target.value)}
              className="px-2 py-1 rounded-md text-xs"
              style={{ background: TH.bg, color: TH.text, border: `1px solid ${TH.border}` }}
              data-testid="gsc-site-select"
            >
              {sites.map((s) => (
                <option key={s.site_url} value={s.site_url}>{s.site_url}</option>
              ))}
            </select>
          </label>
        ) : (
          <span>Property: <strong style={{ color: TH.text }}>{data.property_url}</strong></span>
        )}
        <div className="ml-auto inline-flex items-center gap-2">
          {sitesLoading && <span style={{ color: TH.textDim }}>refreshing sites…</span>}
          {data.mode === "oauth" && (
            <button
              onClick={disconnectGsc}
              className="px-2 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase hover:opacity-80"
              style={{ background: TH.surfaceHi, color: TH.red, border: `1px solid ${TH.red + "60"}` }}
              data-testid="gsc-disconnect-btn"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        {[
          ["Clicks", k.clicks, TH.green],
          ["Impressions", k.impressions, TH.blue],
          ["Avg Position", k.avg_position, TH.accent],
          ["Ranking Pages", k.ranking_pages, TH.purple],
        ].map(([label, m, color]) => (
          <div key={label} className="p-4 rounded-xl border" style={{ background: TH.surface, borderColor: TH.border }}>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: TH.textMute }}>{label}</div>
            <div className="mt-1 text-2xl font-semibold flex items-baseline gap-2" style={{ color }}>
              {fmtN(m?.value ?? 0)}
              <DeltaPill value={m?.delta_pct} />
            </div>
            <div className="text-[11px]" style={{ color: TH.textDim }}>vs prior {data.window_days || 28}d</div>
          </div>
        ))}
      </div>

      {/* Weekly line chart */}
      <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="analytics-weekly-chart">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium inline-flex items-center gap-2"><ChartLine size={16} /> Weekly performance · last 12 weeks</h3>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={weekly}>
              <CartesianGrid stroke={TH.border} strokeDasharray="3 3" />
              <XAxis dataKey="week" tick={{ fill: TH.textMute, fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: TH.textMute, fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: TH.textMute, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: TH.bg, border: `1px solid ${TH.border}`, color: TH.text }} />
              <Legend wrapperStyle={{ color: TH.textMute, fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="clicks" stroke={TH.green} strokeWidth={2} dot={false} name="Clicks" />
              <Line yAxisId="right" type="monotone" dataKey="impressions" stroke={TH.blue} strokeWidth={2} dot={false} name="Impressions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly bar chart */}
      <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="analytics-monthly-chart">
        <h3 className="text-sm font-medium mb-3 inline-flex items-center gap-2"><ChartLine size={16} /> Monthly growth · last 3 months</h3>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={monthly}>
              <CartesianGrid stroke={TH.border} strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fill: TH.textMute, fontSize: 11 }} />
              <YAxis tick={{ fill: TH.textMute, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: TH.bg, border: `1px solid ${TH.border}`, color: TH.text }} />
              <Legend wrapperStyle={{ color: TH.textMute, fontSize: 11 }} />
              <Bar dataKey="clicks" fill={TH.green} name="Clicks" />
              <Bar dataKey="impressions" fill={TH.blue} name="Impressions" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top keywords */}
      <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="analytics-top-queries">
        <h3 className="text-sm font-medium mb-3">Top 20 keywords by clicks · last {data.window_days || 28}d</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.18em] uppercase" style={{ color: TH.textMute, borderBottom: `1px solid ${TH.border}` }}>
              <tr>
                <th className="text-left py-2 pr-3">Query</th>
                <th className="text-right py-2 pr-3 w-20">Clicks</th>
                <th className="text-right py-2 pr-3 w-24">Impressions</th>
                <th className="text-right py-2 pr-3 w-16">CTR</th>
                <th className="text-right py-2 w-20">Avg Pos</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_query || []).map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${TH.border}` }}>
                  <td className="py-2 pr-3">{r.query || "—"}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: TH.green }}>{r.clicks}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: TH.textMute }}>{r.impressions}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: TH.textMute }}>{((r.ctr || 0) * 100).toFixed(1)}%</td>
                  <td className="py-2 text-right" style={{ color: r.position > 30 ? TH.red : TH.text }}>{(r.position || 0).toFixed(1)}</td>
                </tr>
              ))}
              {(!data.by_query || data.by_query.length === 0) && (
                <tr><td colSpan={5} className="py-6 text-center" style={{ color: TH.textDim }}>No clicks yet — first organic traffic appears 7–14 days after publishing.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Needs Rewrite */}
      <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="analytics-needs-rewrite">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium inline-flex items-center gap-2"><Lightning size={16} weight="fill" style={{ color: TH.red }} /> Needs Rewrite · published ≥21 days ago AND (position &gt; 30 OR zero impressions)</h3>
          <button onClick={load} className="text-[11px] inline-flex items-center gap-1" style={{ color: TH.textMute }}><ArrowsClockwise size={11} /> refresh</button>
        </div>
        {needsRewrite.rows.length === 0 ? (
          <p className="text-sm py-4 text-center" style={{ color: TH.textDim }}>Nothing to rewrite — every post ≥3 weeks old is ranking on page 1-3 or hasn't aged out of the cooling-off window yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.18em] uppercase" style={{ color: TH.textMute, borderBottom: `1px solid ${TH.border}` }}>
                <tr>
                  <th className="text-left py-2 pr-3">Post</th>
                  <th className="text-right py-2 pr-3 w-20">Position</th>
                  <th className="text-right py-2 pr-3 w-24">Impressions</th>
                  <th className="text-left py-2 pr-3">Reason</th>
                  <th className="text-right py-2 pr-3 w-20">Rewrites</th>
                  <th className="text-right py-2 w-28">Action</th>
                </tr>
              </thead>
              <tbody>
                {needsRewrite.rows.map((r) => (
                  <tr key={r.post_id} style={{ borderBottom: `1px solid ${TH.border}` }} data-testid={`rewrite-row-${r.post_id}`}>
                    <td className="py-2 pr-3">
                      <Link to={`/blog/${r.slug}`} target="_blank" style={{ color: TH.accent }} className="block truncate max-w-md">{r.title}</Link>
                      <div className="text-[10px]" style={{ color: TH.textDim }}>{r.slug}</div>
                    </td>
                    <td className="py-2 pr-3 text-right" style={{ color: r.position && r.position > 30 ? TH.red : TH.text }}>{r.position ?? "—"}</td>
                    <td className="py-2 pr-3 text-right" style={{ color: TH.textMute }}>{r.impressions}</td>
                    <td className="py-2 pr-3 text-xs" style={{ color: TH.textMute }}>{r.reason}</td>
                    <td className="py-2 pr-3 text-right" style={{ color: TH.textMute }}>{r.rewrite_count}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => rewritePost(r.post_id, r.title)}
                        disabled={busyId === r.post_id}
                        className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                        style={{ background: TH.red + "20", color: TH.red, border: `1px solid ${TH.red + "60"}` }}
                        data-testid={`rewrite-btn-${r.post_id}`}
                      >
                        <Pencil size={11} weight="bold" /> {busyId === r.post_id ? "REWRITING…" : "REWRITE"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rewrite history */}
      {history.length > 0 && (
        <div className="p-5 rounded-2xl border" style={{ background: TH.surface, borderColor: TH.border }} data-testid="analytics-rewrite-history">
          <h3 className="text-sm font-medium mb-3">Recent rewrites · check whether they actually moved the needle</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.18em] uppercase" style={{ color: TH.textMute, borderBottom: `1px solid ${TH.border}` }}>
                <tr>
                  <th className="text-left py-2 pr-3">Post</th>
                  <th className="text-left py-2 pr-3">Fresh angle</th>
                  <th className="text-left py-2 pr-3 w-32">Rewrote at</th>
                  <th className="text-right py-2 w-16">#</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${TH.border}` }}>
                    <td className="py-2 pr-3"><Link to={`/blog/${h.slug}`} target="_blank" style={{ color: TH.accent }}>{h.slug}</Link></td>
                    <td className="py-2 pr-3 text-xs" style={{ color: TH.textMute }}>{h.fresh_angle_keyword}</td>
                    <td className="py-2 pr-3 text-xs" style={{ color: TH.textMute }}>{h.rewrote_at ? new Date(h.rewrote_at).toLocaleString() : ""}</td>
                    <td className="py-2 text-right" style={{ color: TH.textMute }}>{(h.rewrite_count_before || 0) + 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
